import React, { useState } from 'react'
import { useAuth } from '../../AuthContext';
import { app, db, functions } from "../../firebaseConfig";
import { doc, getDoc, collection, query, where, getDocs, Timestamp, limit, addDoc, updateDoc, writeBatch, getCountFromServer, DocumentData, documentId } from 'firebase/firestore';
import { useAppSelector } from '../../redux/store';
import getCurrentScreen from "../../Helper/GetScreen";
import Toast from '../Toast';


interface CollaborateModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedStudent: {
        id: string;
        name: string;
        phone: string;
    };
}

const CollaborateModal = (
    { isOpen, onClose, selectedStudent }: CollaborateModalProps
) => {


    const { userData, currentUser, isAdmin, isManager, isStaff } = useAuth();
    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<{ id: string, name: string }[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [collaborators, setCollaborators] = useState<string[]>([]);
    const [selectedUserDetails, setSelectedUserDetails] = useState<any[]>([]);

    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    
    // Get current collaborators from the selected student
    React.useEffect(() => {
        const fetchCollaborators = async () => {
            if (!selectedStudent || !userData?.companyId) return;
            try {
                const leadRef = doc(db, `companies/${userData.companyId}/leads`, selectedStudent.id);
                const leadDoc = await getDoc(leadRef);

                if (leadDoc.exists()) {
                    const data = leadDoc.data();
                    if (data.collaborators && Array.isArray(data.collaborators)) {
                        setCollaborators(data.collaborators);
                        // Fetch user details for existing collaborators
                        const userDetailsPromises = data.collaborators.map(async (userId: string) => {
                            const userDoc = await getDoc(doc(db, 'users', userId));
                            return { id: userId, name: userDoc.data()?.name || 'Unknown' };
                        });
                        const userDetails = await Promise.all(userDetailsPromises);
                        setSelectedUserDetails(userDetails);
                    }
                }
            } catch (error) {
                console.error('Error fetching collaborators:', error);
            }
        };
        fetchCollaborators();
    }, [selectedStudent, userData?.companyId]);

    const handleSearchUsers = async (value: string) => {
        if (!userData?.companyId || value.length < 3) {
            setSearchResults([]);
            return;
        }

        setIsLoadingUsers(true);
        try {
            const upperValue = value.toUpperCase();
            const usersRef = collection(db, 'users');
            const q = query(
                usersRef,
                where('companyId', '==', userData.companyId),
                where('name', '>=', upperValue),
                where('name', '<=', upperValue + '\uf8ff'),
                limit(10)
            );

            const querySnapshot = await getDocs(q);
            const usersData = querySnapshot.docs
                .filter(doc => doc.data().isActive !== false) // Filter out deactivated users
                .map(doc => ({
                    id: doc.id,
                    name: doc.data().name
                }));

            setSearchResults(usersData);
        } catch (error) {
            console.error('Error searching users:', error);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const handleSelectUser = async (userId: string) => {
        if (!collaborators.includes(userId)) {
            // Add to collaborators array
            setCollaborators([...collaborators, userId]);

            // Add to selected user details
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
                const userData = userDoc.data();
                setSelectedUserDetails(prev => [...prev, {
                    id: userId,
                    name: userData.name
                }]);
            }
        }
    };

    const handleRemoveUser = (userId: string) => {
        setCollaborators(collaborators.filter(id => id !== userId));
        setSelectedUserDetails(prev => prev.filter(user => user.id !== userId));
    };

    const handleCollaborateSubmit = async () => {
        if (!selectedStudent || !userData?.companyId) return;

        try {
            const leadRef = doc(db, `companies/${userData.companyId}/leads`, selectedStudent.id);
            await updateDoc(leadRef, {
                collaborators: collaborators,
                updatedAt: Timestamp.now(),
                updatedBy: currentUser?.uid,
                collaborateDate: Timestamp.fromDate(new Date()), 
            });
            setToastMessage('Collaborators added successfully!');
            setShowToast(true);
            setCollaborators([]);
            setSelectedUserDetails([]);
            setSearchTerm('');
            setSearchResults([]);
            onClose();
        } catch (error) {
            setToastMessage('Failed to add collaborators. Please try again.');
            setShowToast(true);
            console.error('Error updating collaborators:', error);
        }
    };



    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            {isOpen && (
                <div className="bg-white rounded-lg p-6 w-96">
                    <h2 className="text-lg font-semibold mb-4">Add Collaborators</h2>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Student Name</label>
                        <div className="mt-1 p-2 bg-gray-50 rounded-md">
                            {selectedStudent.name}
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                        <div className="mt-1 p-2 bg-gray-50 rounded-md">
                            {selectedStudent.phone}
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Select Collaborators</label>
                        <div className="relative mt-1">
                            <input
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                                placeholder="Type 3 or more characters to search users..."
                                value={searchTerm}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setSearchTerm(value);
                                    handleSearchUsers(value);
                                }}
                            />
                            {isLoadingUsers && (
                                <div className="absolute right-2 top-2">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-600"></div>
                                </div>
                            )}

                            {/* Dropdown of search results */}
                            {searchTerm.length >= 3 && searchResults.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
                                    {searchResults
                                        .filter(user => !collaborators.includes(user.id))
                                        .map(user => (
                                            <div
                                                key={user.id}
                                                className="cursor-pointer hover:bg-gray-100 px-4 py-2"
                                                onClick={() => {
                                                    handleSelectUser(user.id);
                                                    setSearchTerm('');
                                                }}
                                            >
                                                {user.name}
                                            </div>
                                        ))}
                                </div>
                            )}
                        </div>

                        {/* Selected users display */}
                        <div className="mt-2 flex flex-wrap gap-2">
                            {selectedUserDetails.map(user => (
                                <div
                                    key={user.id}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                                >
                                    {user.name}
                                    <button
                                        type="button"
                                        className="flex-shrink-0 ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-cyan-600 hover:bg-cyan-200 hover:text-cyan-500 focus:outline-none"
                                        onClick={() => handleRemoveUser(user.id)}
                                    >
                                        <span className="sr-only">Remove {user.name}</span>
                                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3">
                        <button
                            onClick={() => {
                                onClose();
                                // Reset state
                                setCollaborators([]);
                                setSelectedUserDetails([]);
                                setSearchTerm('');
                                setSearchResults([]);
                            }}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCollaborateSubmit}
                            className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700"
                        >
                            Submit
                        </button>
                    </div>
                </div>
            )}

            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={
                        toastMessage.includes('Error') ||
                        toastMessage.includes('Cannot') ||
                        toastMessage.includes('Failed')
                    }
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}

        </div>
    )
}

export default CollaborateModal
